import {
  EventCouponType,
  EventCreateFormDetailsType,
  EventMetricsType,
  EventPermissionsType,
  EventTicketType,
  REFUND_POLICY_TYPE,
  SingleEventType,
  TICKET_SALES_STATUS,
} from 'types/events/eventTypes';
import { EventTicketMetricsType } from 'types/events/metricsTypes';
import { OrdersListType } from 'types/tickets/tickets.types';

export type EventTicketsPropsType = {
  eventId: string;
  maxNumberOfAttendees?: number;
  ticketTypes: EventTicketType[];
};

export type EventCreateFormDetailsPropsType = {
  eventId: string;
  otherDetails: EventCreateFormDetailsType;
};

export type EventFlyerPropsType = {
  eventId: string;
  flyerBase64: string;
  extension?: string;
};

export type EventCreateFormTaxesPropsType = {
  eventId: string;
  taxDetails: {
    refundPolicy?: REFUND_POLICY_TYPE;
    permitNumber?: string;
    vatRate?: number;
  };
};

export type EventCreateFormTaxesAndContactPropsType = {
  eventId: string;
  taxDetails: {
    refundPolicy?: REFUND_POLICY_TYPE;
    permitNumber?: string | null;
    vatRate?: number;
  };
  contactDetails: {
    email?: string;
    mobileNumber?: string;
    address?: string;
    town?: string;
    postcode?: string;
    country?: string;
  };
};

export type EventResponseType = SingleEventType;

export type EventMetricsResponseType = EventMetricsType;

export type OrdersListResponseType = OrdersListType;

export type OrdersListPropsType = {
  PageNumber?: number;
  PageSize?: number;
  SortByColumn?: string;
  SortType?: string;
  id?: string;
  DateFrom?: string;
  DateTo?: string;
  filter?: string;
};

export type EventPermissionResponseType = EventPermissionsType[];

export type EventTicketMetricsResponseType = EventTicketMetricsType[];

export enum EVENT_FOLLOWERS_SORTBY_ENUM {
  EMAIL = 'email',
  NAME = 'name',
  DATE = 'date',
}

export enum EVENT_STATUS_ENUM {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CLOSED = 'closed',
  PAUSED = 'paused',
  DELETED = 'deleted',
  ACCEPTED_TERMS = 'acceptedTerms',
  SCHEDULED = 'scheduled',
}

export enum SCHEDULE_TYPE_ENUM {
  NON_RECURRING = 'nonRecurring',
  ON_RECURRING_SCHEDULE = 'onRecurringSchedule',
  OPEN_DATE_SCHEDULE = 'openDateSchedule',
  NO_DATE = 'noDate',
}

export type EventDetailedSummaryType = {
  eventFlyer: string;
  eventName: string;
  eventId: string;
  currency: string;
  timeZone: string;
  dateId: string;
  availableTickets: number;
  hasLimit: boolean;
  totalTicketsSold: {
    allTime: number;
    today: number;
  };
  totalSales: {
    allTime: number;
    today: number;
    averageAllTime: number;
  };
  totalComplimentariesIssued: {
    allTime: number;
    today: number;
  };
  ticketTypes: {
    type: string;
    qtyOfTicketsSold: number;
    today: {
      qtyOfTicketsSold: number;
      totalAmount: number;
      numberOfComplimentaries: number;
    };
    maximumNumberOfTickets: number;
    totalAmount: number;
    averageTicketPrice: number;
    numberOfComplimentaries: number;
  }[];
};

export type EventMetricsSalesChannelsType = {
  event: {
    eventFlyer: string;
    eventName: string;
    eventId: string;
    currency: string;
    timeZone: string;
  };
  statistics: {
    name: string;
    valueSold: number;
    qty: number;
  }[];
};

export type EventsSalesChannelsResType = {
  eventName: string;
  eventId: string; // UUID format
  currency: string;
  eventFlyer: string;
  salesChannels: {
    firstName: string;
    profilePic: string;
    lastName: string;
    outlet: {
      name: string;
    };
    linkName: string;
    qty: number;
    value: number;
    referrerType: 'runner' | 'link' | 'userLinkReferrer' | 'direct';
  }[];
}[];

export type EventSeatMetricsType = {
  disabledSeats: number;
  availableTickets: number;
  soldTickets: number;
  standingTickets: number;
  standingTicketsAvailable: number;
  maximumNumberOfAttendees: number;
  seatedTickets: number;
  filledSeatsPercentage: number;
  remaining: number;
  sections: {
    disabledSeats: number;
    availableTickets: number;
    soldTickets: number;
    standingTickets: number;
    standingTicketsAvailable: number;
    maximumNumberOfAttendees: number;
    seatedTickets: number;
    filledSeatsPercentage: number;
    remaining: number;
    sectionName: string;
    message: string;
  }[];
  shows: {
    dateId: string;
    disabledSeats: number;
    availableTickets: number;
    soldTickets: number;
    standingTickets: number;
    standingTicketsAvailable: number;
    maximumNumberOfAttendees: number;
    seatedTickets: number;
    filledSeatsPercentage: number;
    remaining: number;
    showDate: string;
    message: string;
  }[];
};

export type AcceptTermsAndConditionsPropsType = {
  id?: string;
  chargeVAT?: boolean;
  permitNumber?: string;
  vatNumber?: string;
  promoterCompanyName?: string;
  promoterAddress?: string;
  promoterTown?: string;
  promoterMobileNumber?: string;
  promoterEmail?: string;
  ibanNumber?: string;
  bankAccountHolderName?: string;
  promoterPostCode?: string;
};

export type EventCategoriesWithTag = {
  categorized: {
    id: number;
    name: string;
    tags: { id: string; name: string; slug: string }[];
  }[];
  mytags: { id: string; name: string; slug: string }[];
};

export type EventSearchTagResult = {
  tags: {
    name: string;
    slug: string;
    id: string;
  }[];
};

type PaginationType = {
  nextPage: number;
  pageNumber: number;
  pageSize: number;
  previousPage: number;
  totalPages: number;
  totalRecords: number;
  totalRecordsInPage: number;
};

export type EventAttendeesType = {
  scannedDate: string;
  order: {
    refundProtection: string;
    remarks: string;
    referredBy: string;
    gatewayType: string;
    orderId: string;
    orderNo: string;
    currency: string;
    orderDate: string;
    orderTotal: number;
    orderSubTotal: number;
    bookingFee: number;
    paymentType: string;
    orderStatus: number;
    user: {
      firstName: string;
      onBoarded: boolean;
      fullName: string;
      mobileNumber: string;
      lastName: string;
      gender: string;
      userId: string;
      email: string;
      street1: string;
      street2: string;
      countryName: string;
      optInForPromoterPromotionalMaterial: boolean;
      optInForshowsHappeningPromotionalMaterial: boolean;
      dateOfBirth: string;
      dateOfBirthRange: string;
    };
  };
  ticketHolderPersonalInfo: {
    firstName: string;
    lastName: string;
  };
  numberOfPersons: string;
  ticketId: string;
  ticketType: {
    name: string;
    id: string;
    description: string;
  };
  receiptNumber: string;
  subTotal: number;
  bookingFee: number;
  total: number;
  seat: number;
  show: {
    showId: string;
    eventId: string;
    date: string;
    time: string;
  };
  ticketStatus: string;
  barCode: string;
  attendanceDate: string;
  longAttendanceDate: string;
  dateId: string;
  scheduleType: string;
};

export type EventAttendeesResponseType = PaginationType & {
  attendees: EventAttendeesType[];
};

export type EventAttendeesReqType = {
  EventId?: string;
  DateId?: string;
  TicketTypeIds?: string[];
};

export type ZReadingResponseType = PaginationType & {
  transactions: {}[];
};

export type EventTaxReportResponseType = {
  currency: string;
  id: string;
  eventName: string;
  comments: string;
  gross: number;
  qty: number;
  defaultRecipients: string[];
  salesByMonth: {
    month: number;
    year: number;
    gross: number;
    qty: number;
    ticketType: string;
  }[];
};

export type EventTaxReportPropsType = {
  id: string;
  comments?: string;
  recipients?: string[];
  destination: 'PDF' | 'Email';
};

export type SeatingPlansResponseType = {
  name: string;
  id: string;
}[];

export type SeatingPlanDetailResponseType = {
  topPlanSVG: string;
  name: string;
  id: string;
  sections: {
    id: string;
    name: string;
    svg: string;
    sectionType: string;
  }[];
};

interface Sections {
  id: string;
  name: string;
  svg: string;
  sectionType: string;
  ticketType: {
    name: string;
    ticketTypeId: string;
    description: string;
  };
}

export type PlanListTableType = {
  topPlanSVG: string;
  name: string;
  id: string;
  sections: Sections[];
};

export type EventPromoDetailsPropsType = {
  eventId: string;
  promoteDetails: {
    videoLink?: string;
    facebookLink?: string;
    instagramLink?: string;
    googleAnalyticsCode?: string;
    facebookPixelCode?: string;
    ticketTypesToConsiderForPromotionalOffers?: {
      qty: number;
      name: string;
      id: string;
    }[];
    eventArtistTags?: {
      name?: string;
      atName?: string;
      url?: string;
    }[];
    eventSponsorTags?: {
      name?: string;
      atName?: string;
      url?: string;
    }[];
    promotionalQuestionReplies?: {
      name?: string;
      questionId?: string;
      replyValue?: boolean;
    }[];
  };
};

export type EventCouponResponseType = {
  nextPage: 0;
  previousPage: 0;
  pageSize: 0;
  pageNumber: 0;
  totalPages: 0;
  totalRecords: 0;
  totalRecordsInPage: 0;
  coupons: EventCouponType[];
};

type TicketType = {
  name: string;
  ticketTypeId: string;
  description: string;
};

type Section = {
  sectionId: string;
  displayName: string;
};

type Location = {
  locationId: string;
  name: string;
};

type Seat = {
  seatId: string;
  fullDisplayName: string;
  section: Section;
  location: Location;
};

type Show = {
  showId: string;
  eventId: string;
  date: string; // Date string, e.g., "2024-05-08T11:22:53.163Z"
  time: string; // Time string, e.g., "2024-05-08T11:22:53.163Z"
};

type TicketHolderPersonalInfo = {
  firstName: string;
  lastName: string;
};

type Holder = {
  firstName: string;
  fullName: string;
  mobileNumber: string;
  lastName: string;
  userId: string;
  email: string;
};

type TicketCancelled = {
  ticketHolderPersonalInfo: TicketHolderPersonalInfo;
  numberOfPersons: number;
  barcodeVisibilityType: 'Available';
  ticketId: string;
  value: number;
  ticketType: TicketType;
  receiptNumber: string;
  subTotal: number;
  bookingFee: number;
  total: number;
  seat: Seat;
  show: Show;
  ticketStatus: 'Open';
  barcode: string;
  attendanceDate: string;
  longAttendanceDate: string;
  dateId: string;
  scheduleType: 'nonRecurring' | 'onRecurringSchedule' | 'openDateSchedule' | 'noDate';
  holder: Holder;
};

export type OrderCancelResponseType = {
  orderNo: string;
  ticketsCancelled: TicketCancelled[];
  subTotalRefunded: number;
  bookingFeeRefunded: number;
  refundProtectionRefunded: number;
  totalRefunded: number;
};

export type OrderCancelRequestData = {
  ticketIds: string[];
  reason: string;
  email: string;
  password: string;
  notifyUser: boolean;
  bookingFeePercentageRefund: number;
  refundOrderProtection: boolean;
  orderId: string;
};

export type DatesType = {
  id: string;
  eventFlyer: string;
  eventId: string;
  currency: string;
  timeZone: string;
  eventName: string;
  availibilityStatus: string;
  dates: {
    date: string;
    dateId: string;
    timeFrom: string;
    dateTo: string;
    timeTo: string;
    doorsOpenAt: string;
    stopSellingAt: string;
    stopSellingAtUTC: string;
    hint: string;
    status: string;
    pastDate: boolean;
    ticketTypes: {
      id: string;
      name: string;
      status: TICKET_SALES_STATUS;
      price: number;
      maxNumberOfTicketsPerOrder: number;
      minNumberOfTicketsPerOrder: number;
      cannotBuyOnItsOwn: boolean;
      group: {
        imageURL: string;
        name: string;
        groupId: string;
        description: string;
      };
      faceValue: number;
      bookingFee: number;
      remainingTickets: number;
    };
  }[];
};

export type AppliedTicketTypeResponseType = {
  eventId: string;
  ticketType: { name: string; ticketTypeId: string; description: string };
  sectionType: string;
  sectionName: string;
  sectionId: string;
  showDate: string;
  showTime: string;
  eventName: string;
  showId: string;
  ticketTypeSeatsAllocated: {
    ticketTypeName: string;
    colour: string;
    seatId: string;
    ticketTypeId: string;
  }[];
};

export type AppliedTicketTypeRequestType = {
  eventId: string;
  sectionId: string;
  showId?: string;
};

export type GetDisabledSectionRequestType = {
  showId: string;
  sectionId: string;
};

export type ChangeTicketTypeAvailabilityRequest = {
  eventId: string;
  maxAttendees: number | undefined;
  ticketTypes?: {
    availabilityType: TICKET_SALES_STATUS; // Assuming availabilityType can have specific values
    ticketTypeId: string | undefined;
  }[];
  dateIds: string[] | undefined;
};

export type ChangeTicketTypeAvailabilityResponse = {
  id: string;
  eventName: string;
  dates: {
    date: string;
    dateId: string;
    timeFrom: string;
    dateTo: string;
    timeTo: string;
    doorsOpenAt: string;
    status: TICKET_SALES_STATUS;
    pastDate: boolean;
    ticketTypes: {
      id: string;
      name: string;
      status: TICKET_SALES_STATUS;
    }[];
  }[];
};

export enum NOTIFICATION_TYPE_ENUM {
  INFORMATION = 'information',
  WARNING = 'warning',
}

export type NotificationsResType = {
  message: string;
  title: string;
  notificationType: NOTIFICATION_TYPE_ENUM; // Assuming other types may be possible
  id: string;
  eventId: string;
  sortOrder: number;
};

export type ExtraTermsCreateReqType = {
  htmlMessage: string;
  isRequired: boolean;
  showOnTicket: boolean;
  disabled: boolean;
  message: string;
  id?: string;
  eventId: string;
};

export type ExtraTermsType = {
  htmlMessage: string;
  isRequired: boolean;
  showOnTicket: boolean;
  disabled: boolean;
  message: string;
  id: string;
  eventId: string;
};

export type ExtraTermsResType = {
  eventFlyer: string;
  eventName: string;
  eventId: string;
  currency: string;
  timeZone: string;
  eventStatus: EVENT_STATUS_ENUM;
  lastShowDate: string;
  scheduleType: SCHEDULE_TYPE_ENUM;
  promoter: {
    promoterId: string;
    name: string;
    slug: string;
    showsHappeningURL: string;
    email: string;
  };
  url: string;
  terms: ExtraTermsType[];
};

////////////////////////////////////////////////////////////////////////////////////

export type ExtraFiedlGroupingCreateReqType = {
  id?: string;
  name: string;
  showTitle: boolean;
  sortOrder: number;
  eventId: string;
  captureType: 'byOrder' | 'byTicket';
  extraFields?: ExtraField[];
};

export type ExtraFieldGrouping = {
  id: string;
  name: string;
  showTitle: boolean;
  sortOrder: number;
  eventId: string;
  captureType: 'byOrder' | 'byTicket';
  extraFields?: ExtraField[];
};

export type ExtraField = {
  groupId?: string;
  id?: string;
  fieldType: 'string' | 'number' | 'dropdown' | 'textArea' | 'checkBox' | 'fileUpload';
  caption: string;
  optionsAvailable?: string;
  name: string;
  sortOrder: number;
  captionToShowOnTicket: string;
  defaultValue: string;
  helpInformation: string;
};

export type ExtraFiedlCreateReqType = {
  groupId: string;
  id?: string;
  fieldType: 'string' | 'number' | 'dropdown' | 'textArea' | 'checkBox' | 'fileUpload';
  caption: string;
  optionsAvailable: string;
  name: string;
  sortOrder: number;
  captionToShowOnTicket: string;
  defaultValue: string;
  helpInformation: string;
};

export type ExtraFieldGroupingResType = {
  eventFlyer: string;
  eventName: string;
  eventId: string; // UUID
  currency: string;
  timeZone: string;
  eventStatus: EVENT_STATUS_ENUM;
  lastShowDate: string;
  scheduleType: SCHEDULE_TYPE_ENUM;
  promoter: {
    promoterId: string; // UUID
    name: string;
    slug: string;
    showsHappeningURL: string;
    email: string;
  };
  url: string;
  groupings: ExtraFieldGrouping[];
};

/////////////////////////--- Priority Pass type --- ///////////////////

export enum PRIORITY_PASS_STATUS_ENUM {
  ALL = '',
  CREATED = 'created',
  USED = 'used',
  DISABLED = 'disabled',
  DELETED = 'deleted',
}

export type PriorityPassCreateReqType = {
  id?: string;
  email?: string;
  fullName?: string;
  status?: PRIORITY_PASS_STATUS_ENUM;
  neverExpires: boolean;
  ticketTypes: {
    id: string;
    qty: number;
  }[];
  eventId?: string;
};

export type PriorityPassGetReqType = {
  id: string;
  term: string;
  status: PRIORITY_PASS_STATUS_ENUM;
  pageNumber: number;
  pageSize: number;
};

export type PriorityPassType = {
  id: string; // UUID
  email: string;
  fullName: string;
  ticketTypesAllowed: {
    name: string;
    description: string;
    qty: number;
    id: string; // UUID
  }[];
  order: {
    orderNo: string;
    orderDate: string; // ISO date
    orderId: string; // UUID
  };
  status: PRIORITY_PASS_STATUS_ENUM;
  createdAt: string; // ISO date
  createdAtUTC: string; // ISO date
  createdBy: {
    email: string;
    firstName: string;
    lastName: string;
  };
  neverExpires: boolean;
  usedFrom: string;
  usedAt: string; // ISO date
};

export type PriorityPassResType = {
  nextPage: number;
  previousPage: number;
  pageSize: number;
  pageNumber: number;
  totalPages: number;
  totalRecords: number;
  totalRecordsInPage: number;
  event: {
    eventFlyer: string;
    eventName: string;
    eventId: string; // UUID
    currency: string;
    timeZone: string;
    eventStatus: EVENT_STATUS_ENUM;
    lastShowDate: string;
    scheduleType: SCHEDULE_TYPE_ENUM;
    promoter: {
      promoterId: string; // UUID
      name: string;
      slug: string;
      showsHappeningURL: string;
      email: string;
    };
    url: string;
  };
  priorityPasses: PriorityPassType[];
};

export type NotificationCreateReqType = {
  message: string;
  title: string;
  notificationType: NOTIFICATION_TYPE_ENUM; // Assuming other types may be possible
  eventId: string;
  sortOrder: number;
  id?: string;
};

export type PromoterFollower = {
  firstName: string;
  fullName: string;
  mobileNumber: string;
  lastName: string;
  userId: string;
  email: string;
  followingDate: string;
  numberOfTickets: number;
};

export type PromoterFollowersResType = {
  nextPage: number;
  previousPage: number;
  pageSize: number;
  pageNumber: number;
  totalPages: number;
  totalRecords: number;
  totalRecordsInPage: number;
  followers: PromoterFollower[];
};

export type EventFollower = {
  firstName: string;
  lastName: string;
  profilePic: string;
  email: string;
  phoneNumber: string;
  date: number;
};

export type EventFollowersResType = {
  nextPage: number;
  previousPage: number;
  pageSize: number;
  pageNumber: number;
  totalPages: number;
  totalRecords: number;
  totalRecordsInPage: number;
  subscribers: EventFollower[];
};

export type EventReferrerReqType = {
  eventId: string;
  // tag: string;
  email: string;
};

export type EventReferrerResType = {
  promoter: {
    promoterName: string;
    promoterId: string; // UUID format
  };
  firstName: string;
  lastName: string;
  email: string;
};

export type EmailResType = {
  categorized: {
    name: string;
    slug: string;
    id: number;
    priority: number;
    events: {
      eventFlyer: string;
      eventName: string;
      eventId: string;
      currency: string;
      timeZone: string;
      dates: string[];
      priceRange: string;
      currencyCode: string;
      prices: {
        price: number;
      }[];
      categories: {
        name: string;
        slug: string;
        id: number;
        priority: number;
      }[];
      location: {
        venueName: string;
        country: string;
        fullAddress: string;
        latLng: {
          lat: string;
          lng: string;
        };
      };
      tags: {
        name: string;
        slug: string;
        id: string;
      }[];
      eventPublishedDate: string;
      promoter: {
        promoterId: string;
        name: string;
        slug: string;
        promoterShowsHappeningPageURL: string;
        email: string;
        logo: string;
        showsHappeningURL: string;
        userFollowStatus: 'NotAvailable' | string;
      };
      venue: string;
      url: string;
      summary: string;
      description: string;
      dateFriendlyText: string;
    }[];
  }[];
};

export function serializeParams(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        searchParams.append(key, v);
      });
    } else if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });

  return searchParams.toString();
}
